/**
 * PATH: packages/job-hub/src/jobs/client/resolveCollectionRoutes.ts
 * FILE_ID: JOBHUB-JOBS-CLIENT-COLLECT-001
 * PURPOSE: Route-table segment 1 — invoice-candidate synthetics + the id-less
 *          collection reads/creates (stages defs, quotes, invoices, jobs list
 *          + create, export/bulk rejects, clients lookup).
 * WHY: Moved VERBATIM from djangoJobHubClient.ts (S4 rank 12). This segment
 *      MUST run before the schedule + job segments: /jobs (bare) must be
 *      answered here before /jobs/:id catch-alls later.
 */
import type { SegmentResolver } from './routeTypes'
import { reshapeV2JobsPayload } from '../../jobs-list/v2JobsAdapter'

export const resolveCollectionRoutes: SegmentResolver = ({
  verb,
  match,
  tenantParam,
  requestParams,
}) => {
  // --- invoice candidates (sam-api AI extraction; not on standard API) -
  // The hub polls /extracted-invoices ALWAYS-ON to badge the Invoices
  // tab. The standard platform has no extraction pipeline yet, so answer
  // the list empty (badge falls back to the real invoice count) and
  // reject the per-candidate actions (never reached with an empty list).
  if (match('/extracted-invoices') && verb === 'get') {
    return { kind: 'synthetic', synthetic: [] }
  }
  const ID = '([^/?]+)'
  if (
    match(`/extracted-invoices/${ID}`) ||
    match(`/extracted-invoices/${ID}/approve`) ||
    match(`/extracted-invoices/${ID}/reject`) ||
    match(`/invoices/${ID}/push-to-xero`)
  ) {
    return { kind: 'reject', rejectReason: 'Invoice extraction is not available on the standard platform yet.' }
  }

  // --- collections without an id -------------------------------------
  if (match('/stages/definitions')) {
    return { kind: 'http', method: 'get', url: '/api/report/stages/definitions/' }
  }
  if (match('/quotes') && verb === 'get') {
    // Per-job read (rank 10, jobs-hub-fix v2): the hub passes ?job_id, so
    // serve the job-scoped endpoint instead of shipping the whole org's
    // quote book to the client. Param-less callers keep the org-wide list.
    if (requestParams?.job_id != null) {
      return {
        kind: 'http',
        method: 'get',
        url: `/api/report/job/${requestParams.job_id}/quotes/`,
      }
    }
    return { kind: 'http', method: 'get', url: '/api/report/quotes/hub/' }
  }
  if (match('/invoices') && verb === 'get') {
    return { kind: 'http', method: 'get', url: '/api/report/invoices/hub/' }
  }
  // Next free display number (J{YY}{N}). Must precede bare /jobs GET/POST.
  if (match('/jobs/next-number') && verb === 'get') {
    return {
      kind: 'http',
      method: 'get',
      url: '/api/v2/jobs/next-number/',
      params: {
        ...(tenantParam ?? {}),
        ...(requestParams?.parent ? { parent: String(requestParams.parent) } : {}),
      },
    }
  }
  if (match('/jobs') && verb === 'get') {
    // Contract-sibling lookup (?parent_job_id=…): Django jobs aren't
    // envelope-grouped -> empty, exactly as before W5b.
    if (requestParams?.parent_job_id != null) {
      return { kind: 'synthetic', synthetic: [] }
    }
    // W5b — the bare jobs LIST read (shared JobsHubListPage /
    // JobsTrackerPage via useJobsListData). Serve the real V2 list
    // (same endpoint + page_size the master Redux thunks use) and
    // reshape each row into the JobListRow contract, carrying the
    // W5a served KPI fields (wip_earned / at_risk_overdue /
    // health_score / health_band) through untouched.
    return {
      kind: 'http',
      method: 'get',
      url: '/api/v2/jobs/',
      params: { page_size: 5000, ...(tenantParam ?? {}) },
      reshape: reshapeV2JobsPayload,
    }
  }
  // S2 rank 6 — shared CreateJobModal create. Org scoping rides ?tenant=
  // (JobsV2ListAPI.post resolves the org the same way as its GET). Fields
  // the Job model can't store (contract_value/win_status/year) are ignored
  // server-side; non-approvers get a 202 review-queue envelope.
  if (match('/jobs') && verb === 'post') {
    return { kind: 'http', method: 'post', url: '/api/v2/jobs/', params: tenantParam }
  }
  // Jobs V2 export owns filtering, row authorization, workbook formatting,
  // and formula neutralisation. The caller supplies the ordered JSON body.
  if (match('/jobs/export/excel') && verb === 'post') {
    return {
      kind: 'http',
      method: 'post',
      url: '/api/v2/jobs/export/excel/',
      params: tenantParam,
    }
  }
  if (match('/jobs/export/excel')) {
    return {
      kind: 'reject',
      rejectReason: 'Jobs Excel export requires a POST request.',
    }
  }
  // Bulk endpoints are sam-api-only (qb!2692); the shared list gates
  // multi-select behind capabilities.bulkJobUpdates so these should
  // never be reached on the standard platform — reject loudly if so.
  if (match('/jobs/bulk-update') || match('/jobs/bulk-delete')) {
    return { kind: 'reject', rejectReason: 'Bulk job updates are not available on the standard platform yet.' }
  }
  // Clients lookup (right-click "Move to client…" + CreateJobModal):
  // serve the org-scoped platform clients list reshaped to the hub's
  // ClientOption contract ({id, name}). The endpoint paginates
  // (LookupPagination, max 5000) — one page covers real client books.
  // Inline client creation (ClientCombobox "+ New client"): the platform
  // clients endpoint accepts POST {client_name} and returns the created
  // row (201) — same contract the workforce JobQuickCreate flow verified
  // against report/tests/test_job_creation_manual.py. Reshape to the
  // hub's ClientOption ({id, name}) so the combobox can select it
  // immediately.
  if (match('/clients') && verb === 'post') {
    return {
      kind: 'http',
      method: 'post',
      url: '/api/report/clients/',
      params: tenantParam,
      reshape: (data) => {
        const row = (data ?? {}) as Record<string, unknown>
        return { id: row.id, name: row.client_name ?? row.name ?? '' }
      },
    }
  }
  if (match('/clients') && verb === 'get') {
    return {
      kind: 'http',
      method: 'get',
      url: '/api/report/clients/',
      params: { page_size: 5000, ...(tenantParam ?? {}) },
      reshape: (data) => {
        const rows = (Array.isArray(data)
          ? data
          : ((data as { results?: unknown[] } | null)?.results ?? [])
        ) as Array<Record<string, unknown>>
        return rows.map((c) => ({ id: c.id, name: c.client_name ?? c.name ?? '' }))
      },
    }
  }

  // Org-member autocomplete for the job Team & Access picker — the same
  // @mention search the communication hub uses (org-scoped, max 20).
  if (match('/user-search') && verb === 'get') {
    return { kind: 'http', method: 'get', url: '/api/report/user-search/' }
  }

  if (match('/access/invite') && verb === 'post') {
    return { kind: 'http', method: 'post', url: '/api/access/invite/' }
  }

  return null
}
